// Phase 35 (C5 / D6) — Settings → Invoice e-export: Peppol provider/format picker.
//
// FORMAT comes from the server invoiceExport registry (GET
// /firm-settings/export-formats → listExporters()); the chosen formatId is
// stored on FirmSettings.invoiceExportFormat. PROVIDER (transmission) is
// "Download (manual)" only — there is no transmission/Billit client yet. See
// docs/architecture/peppol-provider-integration.md for the seam a real
// transmission adapter would implement. Admin-only (server enforces it too).
//
// Self-contained component (stable identity) per CLAUDE.md "Known patterns".

import { useState, useEffect } from "react";
import { api } from "../api";
import { useTranslation } from "../i18n";
import { FS } from "../lib/tokens";
import MenuSelect from "./MenuSelect"; // 154B §2

type InvoiceExportFormatPanelProps = {
  isAdmin?: boolean;
  toast?: (msg: string, kind?: string) => void;
};

export default function InvoiceExportFormatPanel({ isAdmin, toast }: InvoiceExportFormatPanelProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [formats, setFormats] = useState<any[]>([]);
  const [providers, setProviders] = useState<any[]>([{ id: "download", displayName: "Download (manual)" }]);
  const [current, setCurrent] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api<{ formats?: unknown[]; providers?: unknown[] }>("/firm-settings/export-formats"),
      api<{ invoiceExportFormat?: string | null }>("/firm-settings"),
    ])
      .then(([fmt, fs]) => {
        if (cancelled) return;
        setFormats(Array.isArray(fmt?.formats) ? fmt.formats : []);
        if (Array.isArray(fmt?.providers) && fmt.providers.length) setProviders(fmt.providers);
        setCurrent(fs?.invoiceExportFormat || "");
      })
      .catch(() => {
        if (!cancelled) setCurrent("");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (formatId: string) => {
    setCurrent(formatId);
    setSaving(true);
    try {
      await api("/firm-settings", {
        method: "PUT",
        body: JSON.stringify({ invoiceExportFormat: formatId }),
      });
      toast?.(
        t("settings.export.saved", { fallback: "Invoice export format saved" }),
      );
    } catch (e) {
      toast?.("⚠ " + ((e as any).message || t("common.save_failed", { fallback: "Save failed" })), "error");
    }
    setSaving(false);
  };

  return (
    <div className="detail-card settings-pane" style={{ order: 76 }}>
      <h4
        className="settings-pane-title"
        style={{ margin: 0, cursor: "pointer" }}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="settings-chevron">{open ? "▼" : "▶"}</span>
        {t("settings.export.title", {
          fallback: "Invoice e-export — Peppol format",
        })}
      </h4>
      {open && (
        <>
          <p
            style={{
              fontSize: FS.s11_5,
              color: "var(--ts)",
              margin: "6px 0 10px",
              lineHeight: 1.6,
            }}
          >
            {t("settings.export.desc", {
              fallback:
                "The structured format generated when an invoice is exported. Transmission is download-only for now — there is no automatic send to a Peppol access point yet.",
            })}
          </p>
          {current === null ? (
            <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
              {t("settings.label.loading", { fallback: "Loading…" })}
            </p>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
                gap: 14,
              }}
            >
              <div className="field">
                <label>
                  {t("settings.export.format_label", { fallback: "Format" })}
                </label>
                <MenuSelect
                  className="field-input"
                  value={current}
                  disabled={!isAdmin || saving}
                  onChange={(v) => save(v)}
                  options={[
                    /* show any stored value even if the registry no longer
                       lists it — this site had already written 05m-b's rule for
                       itself, and it stays written rather than being deferred
                       to MenuSelect's closed face, which shows an unmatched
                       value but cannot offer it as a row */
                    ...(current && !formats.some((f) => f.formatId === current)
                      ? [[current, current] as const]
                      : []),
                    ...formats.map(
                      (f) => [String(f.formatId), String(f.displayName)] as const,
                    ),
                  ]}
                />
              </div>
              <div className="field">
                <label>
                  {t("settings.export.provider_label", {
                    fallback: "Transmission",
                  })}
                </label>
                <MenuSelect
                  className="field-input"
                  value="download"
                  disabled
                  onChange={() => {}}
                  options={providers.map(
                    (p) => [String(p.id), String(p.displayName)] as const,
                  )}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
